import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Grid, Box, Modal } from '@mui/material'
import BackHeader from '../../components/BackHeader/BackHeader'
import Spacer from '../../components/Spacer/Spacer'
import { Notification } from '../../components/shared'
import Settings from './Settings/Settings'
import { FirstCategory, SecondCategory } from './YatzyProperties'
import { yatzyPoints } from './YatzyPoints'
import Leaderboard from './components/Leaderboard/Leaderboard'
import MobileLeaderboard from './components/MobileLeaderboard/MobileLeaderboard'
import ScoreTable from './components/ScoreTable/ScoreTable'
import CategoryModal from './components/CategoryModal/CategoryModal'
import RulesDialog from './RulesDialog'
import TurnCup from './components/TurnCup'
import useWakeLock from '../../hooks/useWakeLock'
import yatzyBackground from '../../images/yatzyBackground1.png'
import yatzyLogo from '../../images/yatzyTrans.png'

import './Yatzy.css'

const STORAGE_KEY = 'svirr-yatzy'
const MAX_PLAYERS = 10

function createPlayer(index, name = '') {
  return {
    id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
    name: name || `Spiller ${index + 1}`,
    firstCategoryScores: {},
    secondCategoryScores: {},
    disabledCategories: {},
  }
}

function readSavedGame() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw)
    if (!Array.isArray(saved.players) || saved.players.length < 1) return null
    const players = saved.players.slice(0, MAX_PLAYERS).map((player, index) => ({
      id: player.id || createPlayer(index).id,
      name: typeof player.name === 'string' && player.name.trim() ? player.name : `Spiller ${index + 1}`,
      firstCategoryScores: player.firstCategoryScores && typeof player.firstCategoryScores === 'object' ? player.firstCategoryScores : {},
      secondCategoryScores: player.secondCategoryScores && typeof player.secondCategoryScores === 'object' ? player.secondCategoryScores : {},
      disabledCategories: player.disabledCategories && typeof player.disabledCategories === 'object' ? player.disabledCategories : {},
    }))
    const gameType = saved.gameType === 'sixDice' ? 'sixDice' : 'fiveDice'
    const tab = Math.min(Math.max(0, Number(saved.tab) || 0), players.length - 1)
    const storedTurn = Number(saved.turn)
    const turn = Math.min(Math.max(0, Number.isFinite(storedTurn) ? storedTurn : tab), players.length - 1)
    return {
      players,
      numberOfPlayers: players.length,
      gameType,
      tab,
      turn,
    }
  } catch {
    return null
  }
}

function activeCategoryNames(gameType) {
  return [...FirstCategory, ...SecondCategory]
    .filter((item) => item.maxValues[gameType] != null)
    .map((item) => item.categoryName)
}

function isCategoryFilled(player, categoryName) {
  return Boolean(player.disabledCategories[categoryName])
    || player.firstCategoryScores[categoryName] !== undefined
    || player.secondCategoryScores[categoryName] !== undefined
}

function isGameFinished(playerList, gameType) {
  const names = activeCategoryNames(gameType)
  return playerList.length > 0 && playerList.every((player) => names.every((name) => isCategoryFilled(player, name)))
}

const YatzyGame = () => {
  const navigate = useNavigate()
  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated && !!state.auth.token && !!state.mate.currentUser)
  const savedGame = useState(() => readSavedGame())[0]
  const [players, setPlayers] = useState(() => savedGame?.players || [createPlayer(0)])
  const [numberOfPlayers, setNumberOfPlayers] = useState(() => savedGame?.numberOfPlayers || 1)
  const [gameType, setGameType] = useState(() => savedGame?.gameType || 'fiveDice')
  const [minValueForBonus, setMinValueForBonus] = useState(() => (savedGame?.gameType === 'sixDice' ? 84 : 63))
  const [tab, setTab] = useState(() => savedGame?.tab || 0)
  const [turn, setTurn] = useState(() => savedGame?.turn ?? savedGame?.tab ?? 0)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [category, setCategory] = useState('')
  const [placement, setPlacement] = useState('')
  const [showTurnDialog, setShowTurnDialog] = useState(false)
  const [lastTurn, setLastTurn] = useState(() => savedGame?.turn ?? savedGame?.tab ?? 0)
  const [showGameOverModal, setShowGameOverModal] = useState(false)
  const [isGameAction, setIsGameAction] = useState(false)
  const [showRules, setShowRules] = useState(false)
  const [pendingEdit, setPendingEdit] = useState('')
  const [editMode, setEditMode] = useState(false)
  const turnTimer = useRef(null)

  useEffect(() => () => window.clearTimeout(turnTimer.current), [])

  useWakeLock()

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        players,
        gameType,
        tab,
        turn,
      }))
    } catch {
      // Spillet kan stadig bruges, selv om browseren afviser lagring.
    }
  }, [players, gameType, tab, turn])

  useEffect(() => {
    setPlayers((current) => {
      if (numberOfPlayers === current.length) return current
      if (numberOfPlayers > current.length) {
        return [
          ...current,
          ...Array.from({ length: numberOfPlayers - current.length }, (_, index) => createPlayer(current.length + index)),
        ]
      }
      return current.slice(0, numberOfPlayers)
    })
  }, [numberOfPlayers])

  useEffect(() => {
    setMinValueForBonus(gameType === 'sixDice' ? 84 : 63)
    const hidden = new Set(
      [...FirstCategory, ...SecondCategory]
        .filter((item) => item.maxValues[gameType] == null)
        .map((item) => item.categoryName)
    )
    if (hidden.size === 0) return
    setPlayers((current) => current.map((player) => {
      const secondCategoryScores = { ...player.secondCategoryScores }
      const disabledCategories = { ...player.disabledCategories }
      let changed = false
      hidden.forEach((name) => {
        if (name in secondCategoryScores) {
          delete secondCategoryScores[name]
          changed = true
        }
        if (name in disabledCategories) {
          delete disabledCategories[name]
          changed = true
        }
      })
      return changed ? { ...player, secondCategoryScores, disabledCategories } : player
    }))
  }, [gameType])

  useEffect(() => {
    const lastIndex = Math.max(0, numberOfPlayers - 1)
    if (tab >= numberOfPlayers) setTab(lastIndex)
    if (turn >= numberOfPlayers) setTurn(lastIndex)
  }, [numberOfPlayers, tab, turn])

  useEffect(() => {
    if (turn !== lastTurn) {
      if (isGameAction) setShowTurnDialog(true)
      setLastTurn(turn)
      setIsGameAction(false)
    }
  }, [turn, lastTurn, isGameAction])

  // Function to filter categories based on gameType
  const getFilteredCategories = useCallback((categories) => {
    return categories.filter(category => {
      return category.maxValues[gameType] !== null
    })
  }, [gameType])

  const lowerNames = useMemo(
    () => SecondCategory.filter((item) => item.maxValues[gameType] != null).map((item) => item.categoryName),
    [gameType]
  )

  const calculateScore = (scoreObject) => {
    return Object.values(scoreObject).reduce((acc, value) => acc + (Number(value) || 0), 0)
  }

  const calculateTotal = useCallback((player) => {
    const upper = calculateScore(player.firstCategoryScores)
    const lower = lowerNames.reduce((sum, name) => sum + (Number(player.secondCategoryScores[name]) || 0), 0)
    return upper + lower + (upper >= minValueForBonus ? 50 : 0)
  }, [lowerNames, minValueForBonus])

  const handlePlayerNameChange = (event, index) => {
    const name = event.target.value
    setPlayers((current) => current.map((player, playerIndex) => (
      playerIndex === index ? { ...player, name } : player
    )))
  }

  const advanceTurn = useCallback((playerIndex) => {
    if (numberOfPlayers < 2) return
    window.clearTimeout(turnTimer.current)
    turnTimer.current = window.setTimeout(() => {
      const next = (playerIndex + 1) % numberOfPlayers
      setIsGameAction(true)
      setTurn(next)
      setTab(next)
    }, 400)
  }, [numberOfPlayers])

  const finishOrAdvance = useCallback((nextPlayers, playerIndex, shouldAdvance) => {
    if (isGameFinished(nextPlayers, gameType)) {
      window.clearTimeout(turnTimer.current)
      setShowGameOverModal(true)
      return
    }
    if (shouldAdvance) advanceTurn(playerIndex)
  }, [advanceTurn, gameType])

  const updateScore = useCallback(({ playerIndex, scoreType, categoryName, value, keepTurn = false }) => {
    const key = scoreType === 'first' ? 'firstCategoryScores' : 'secondCategoryScores'
    const player = players[playerIndex]
    if (!player) return
    const already = player[key][categoryName] !== undefined || Boolean(player.disabledCategories[categoryName])
    const nextPlayers = players.map((item, index) => {
      if (index !== playerIndex) return item
      const disabledCategories = { ...item.disabledCategories }
      delete disabledCategories[categoryName]
      return {
        ...item,
        [key]: { ...item[key], [categoryName]: value },
        disabledCategories,
      }
    })
    setPlayers(nextPlayers)
    finishOrAdvance(nextPlayers, playerIndex, !keepTurn && !already && playerIndex === turn)
  }, [players, finishOrAdvance, turn])

  const disableCategory = useCallback((playerIndex, scoreType, categoryName, keepTurn = false) => {
    const key = scoreType === 'first' ? 'firstCategoryScores' : 'secondCategoryScores'
    const player = players[playerIndex]
    if (!player || player.disabledCategories[categoryName]) return
    const alreadyScored = player[key][categoryName] !== undefined
    const nextPlayers = players.map((item, index) => {
      if (index !== playerIndex) return item
      return {
        ...item,
        [key]: { ...item[key], [categoryName]: 0 },
        disabledCategories: { ...item.disabledCategories, [categoryName]: true },
      }
    })
    setPlayers(nextPlayers)
    finishOrAdvance(nextPlayers, playerIndex, !keepTurn && !alreadyScored && playerIndex === turn)
  }, [players, finishOrAdvance, turn])

  const removeDisabledCategory = useCallback((playerIndex, categoryName) => {
    setPlayers((current) => current.map((item, index) => {
      if (index !== playerIndex) return item
      const disabledCategories = { ...item.disabledCategories }
      const firstCategoryScores = { ...item.firstCategoryScores }
      const secondCategoryScores = { ...item.secondCategoryScores }
      delete disabledCategories[categoryName]
      delete firstCategoryScores[categoryName]
      delete secondCategoryScores[categoryName]
      return { ...item, disabledCategories, firstCategoryScores, secondCategoryScores }
    }))
    setShowGameOverModal(false)
  }, [])

  function resetGameKeepNames() {
    window.clearTimeout(turnTimer.current)
    setPlayers((current) => current.map((player, index) => ({
      ...createPlayer(index, player.name),
      id: player.id,
      name: player.name,
    })))
    setTab(0)
    setTurn(0)
    setShowCategoryModal(false)
    setShowTurnDialog(false)
    setShowGameOverModal(false)
  }

  // Render the score table
  const renderCategoryRows = useCallback(
    (categories, scoreType, isFirstCategory = false) => {
      const currentPlayer = players[tab]
      if (!currentPlayer) return null
      return getFilteredCategories(categories).map((category) => {
        const scratched = Boolean(currentPlayer.disabledCategories[category.categoryName])
        const score = currentPlayer[`${scoreType}CategoryScores`][category.categoryName]
        const filled = score !== undefined && !scratched
        const asksToCorrect = filled || scratched || tab !== turn
        const waiting = pendingEdit === category.categoryName

        const openScore = (correcting) => {
          setEditMode(correcting)
          setPendingEdit('')
          setShowCategoryModal(true)
          setCategory(category.categoryName)
          setPlacement(isFirstCategory ? 'first' : 'second')
        }

        return (
          <tr key={category.categoryName}>
            <td className="category-name">{category.prettyName}</td>
            <td className="category-max">{`(${category.maxValues[gameType]})`}</td>
            <td className="category-score" style={{ textAlign: 'center' }}>
              <div
                className={`score-field${filled ? ' is-filled' : ''}${scratched ? ' is-scratched' : ''}${waiting ? ' is-correcting' : ''}`}
                onClick={() => {
                  if (asksToCorrect) {
                    setPendingEdit(category.categoryName)
                    return
                  }
                  openScore(false)
                }}
              >
                {scratched ? '0' : score === undefined ? '' : score}
              </div>
            </td>
            <td className="category-action" style={{ textAlign: 'center' }}>
              {waiting ? (
                <button type="button" className="score-correct" onClick={() => openScore(true)}>
                  Ret
                </button>
              ) : null}
            </td>
          </tr>
        )
      })
    },
    [players, tab, turn, pendingEdit, gameType, getFilteredCategories, setShowCategoryModal, setCategory, setPlacement]
  )

  const bestScore = players.reduce((best, player) => Math.max(best, calculateTotal(player)), 0)
  const winners = players.filter((player) => calculateTotal(player) === bestScore)
  const currentPlayer = players[tab] || players[0]
  const turnPlayer = players[turn] || players[0]
  const viewingOther = tab !== turn
  const fieldsLeft = currentPlayer
    ? activeCategoryNames(gameType).filter((name) => !isCategoryFilled(currentPlayer, name)).length
    : 0

  return (
    <div className={`yatzy-root${isLoggedIn ? '' : ' yatzy-root--guest'}`}>
      <img className="yatzy-background" src={yatzyBackground} alt="" />
      <div className="yatzy-top">
        <BackHeader
          title={
            <span className="yatzy-bar-brand">
              <span aria-hidden="true">🎲</span>
              <img src={yatzyLogo} alt="Yatzy" />
              <span aria-hidden="true">🎲</span>
            </span>
          }
          subtitle={viewingOther ? `Det er ${turnPlayer?.name || 'spil'}s tur` : 'Det er din tur'}
          onBack={() => navigate(isLoggedIn ? '/spil' : '/')}
          backLabel={isLoggedIn ? 'Tilbage til spil' : 'Tilbage'}
          actions={
            <Settings
              numberOfPlayers={numberOfPlayers}
              setNumberOfPlayers={setNumberOfPlayers}
              gameType={gameType}
              setGameType={setGameType}
              onResetGame={resetGameKeepNames}
            />
          }
        />
        <div className="yatzy-turn">
          <div className="yatzy-turn-copy">
            <div className="yatzy-turn-line">
              <input
                className="yatzy-turn-who"
                value={currentPlayer?.name || ''}
                placeholder="Skriv navn"
                aria-label="Spillernavn"
                onChange={(event) => handlePlayerNameChange(event, tab)}
              />
              <button type="button" className="yatzy-rules-button" onClick={() => setShowRules(true)}>
                Regler
              </button>
            </div>
          </div>
        </div>
      </div>

      <Grid container spacing={3} className="yatzy-board" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={3} className="yatzy-leaderboard-slot">
          <Leaderboard 
            players={players}
            calculateTotal={calculateTotal}
            onPlayerClick={(playerIndex) => {
              setPendingEdit('')
              setTab(playerIndex)
            }}
            tab={tab}
            turn={turn}
          />
        </Grid>
        <Grid item xs={12} md={9} sx={{ zIndex: 1 }}>
          <MobileLeaderboard 
            players={players}
            calculateTotal={calculateTotal}
            onPlayerClick={(playerIndex) => {
              setPendingEdit('')
              setTab(playerIndex)
            }}
            tab={tab}
            turn={turn}
          />
          <ScoreTable 
            players={players}
            tab={tab}
            gameType={gameType}
            minValueForBonus={minValueForBonus}
            renderCategoryRows={renderCategoryRows}
            calculateScore={calculateScore}
            calculateTotal={calculateTotal}
            disableCategory={disableCategory}
            removeDisabledCategory={removeDisabledCategory}
            setShowCategoryModal={setShowCategoryModal}
            setCategory={setCategory}
            setPlacement={setPlacement}
            FirstCategory={FirstCategory}
            SecondCategory={SecondCategory}
          />
        </Grid>
      </Grid>
  
      <Spacer l />
  
      <CategoryModal
        open={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false)
          setEditMode(false)
        }}
        category={category}
        placement={placement}
        updateScore={updateScore}
        playerIndex={tab}
        gameType={gameType}
        yatzyPoints={yatzyPoints}
        correcting={editMode}
        onZero={() => {
          disableCategory(tab, placement, category, editMode)
          setShowCategoryModal(false)
          setEditMode(false)
        }}
        onRemove={() => {
          removeDisabledCategory(tab, category)
          setShowCategoryModal(false)
          setEditMode(false)
          setPendingEdit('')
        }}
      />

      <RulesDialog open={showRules} onClose={() => setShowRules(false)} gameType={gameType} />

      <Modal
        open={showGameOverModal}
        onClose={() => setShowGameOverModal(false)}
        aria-labelledby="game-over-modal-title"
        aria-describedby="game-over-modal-description"
      >
        <Box className="gameover-modal">
          <h2 id="game-over-modal-title">Spillet er slut</h2>
          <p className="gameover-label">{winners.length > 1 ? 'Uafgjort' : 'Vinder'}</p>
          <p className="gameover-name">{winners.map((player) => player.name).join(' og ')}</p>
          <p className="gameover-score">med {bestScore} point</p>
          <div className="gameover-actions">
            <button
              type="button"
              className="gameover-action"
              onClick={() => {
                setShowGameOverModal(false)
                resetGameKeepNames()
              }}
            >
              Nyt spil
            </button>
            <button
              type="button"
              className="gameover-action gameover-action--quiet"
              onClick={() => setShowGameOverModal(false)}
            >
              Luk
            </button>
          </div>
        </Box>
      </Modal>

      <Notification 
        open={showTurnDialog}
        onClose={() => setShowTurnDialog(false)}
        message={`Det er ${turnPlayer?.name || 'næste spiller'}s tur`}
        icon="🎲"
        type="info"
        autoHideDuration={2200}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
      />

    </div>
  )
}

export default YatzyGame